import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Play,
  Square,
  Trash2,
  Eye,
  Zap,
  Clock,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Layers
} from 'lucide-react';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import {
  dynamicAnalysisApi,
  type DynamicAnalysisJob,
  getScheduleLabel
} from '../../services/dynamic-analysis-api';
import { useTimezone } from '../../contexts/TimezoneContext';
import { handleApiError } from '../../services/api';
import toast from 'react-hot-toast';

interface JobCardProps {
  job: DynamicAnalysisJob;
  onDelete: (job: DynamicAnalysisJob) => void;
  onToggleStatus: (job: DynamicAnalysisJob) => void;
  onViewDetails: (job: DynamicAnalysisJob) => void;
  onRunNow: (job: DynamicAnalysisJob) => void;
}

const JobCard: React.FC<JobCardProps> = ({
  job,
  onDelete,
  onToggleStatus,
  onViewDetails,
  onRunNow
}) => {
  const { formatRelativeTime } = useTimezone();
  const isActive = job.enabled && job.status === 'active';

  const getStatusVariant = () => {
    if (isActive) return 'success';
    if (job.status === 'error') return 'error';
    return 'neutral';
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-lg font-semibold text-foreground">
                {job.name || 'Unnamed Job'}
              </h3>
              <Badge variant={getStatusVariant()}>
                {isActive ? 'Active' : (job.status || 'inactive')}
              </Badge>
            </div>
            {(job.description || job.config?.analysisDescription) && (
              <p className="text-sm text-muted-foreground mb-2">
                {job.description || job.config?.analysisDescription}
              </p>
            )}
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {getScheduleLabel(job.schedule)}
              </span>
              {job.lastRunAt && (
                <span>Last run: {formatRelativeTime(job.lastRunAt)}</span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onViewDetails(job)}
              className="text-muted-foreground hover:text-foreground"
              title="View Details"
            >
              <Eye className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onRunNow(job)}
              className="text-info hover:text-info/80"
              title="Run Now"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(job)}
              className="text-destructive hover:text-destructive/80"
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Configuration Summary */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4 text-sm">
          <div>
            <span className="text-muted-foreground">Schedule:</span>
            <p className="font-medium text-foreground">
              {getScheduleLabel(job.schedule)}
            </p>
          </div>
          <div>
            <span className="text-muted-foreground">Refresh Mode:</span>
            <p className="font-medium text-foreground">
              {job.config?.dynamicAnalysisSettings?.refreshOnSchedule ? 'Full Refresh' : 'Incremental'}
            </p>
          </div>
          <div>
            <span className="text-muted-foreground">Type:</span>
            <p className="font-medium text-foreground">
              Auto Dynamic
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="text-xs text-muted-foreground">
            Updated {job.updatedAt ? formatRelativeTime(job.updatedAt) : 'recently'}
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onToggleStatus(job)}
              className={`flex items-center gap-2 ${
                isActive
                  ? 'text-warn hover:text-warn/80'
                  : 'text-success hover:text-success/80'
              }`}
            >
              {isActive ? (
                <>
                  <Square className="w-4 h-4" />
                  Disable
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  Enable
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};

const DynamicAnalysisPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Fetch jobs
  const { data, isLoading, error } = useQuery({
    queryKey: ['dynamic-analysis-jobs'],
    queryFn: dynamicAnalysisApi.getOrganizationJobs,
    refetchInterval: 30000
  });

  // Toggle status mutation
  const toggleStatusMutation = useMutation({
    mutationFn: async ({ jobId, enable }: { jobId: string; enable: boolean }) => {
      return enable
        ? dynamicAnalysisApi.enableJob(jobId)
        : dynamicAnalysisApi.disableJob(jobId);
    },
    onSuccess: (_, { enable }) => {
      toast.success(`Job ${enable ? 'enabled' : 'disabled'} successfully`);
      queryClient.invalidateQueries({ queryKey: ['dynamic-analysis-jobs'] });
    },
    onError: (error) => {
      toast.error(handleApiError(error) || 'Failed to update job status');
    }
  });

  // Run job mutation
  const runJobMutation = useMutation({
    mutationFn: dynamicAnalysisApi.runJob,
    onSuccess: () => {
      toast.success('Job execution started');
      queryClient.invalidateQueries({ queryKey: ['dynamic-analysis-jobs'] });
    },
    onError: (error) => {
      toast.error(handleApiError(error) || 'Failed to run job');
    }
  });

  // Delete job mutation
  const deleteMutation = useMutation({
    mutationFn: dynamicAnalysisApi.deleteJob,
    onSuccess: () => {
      toast.success('Job deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['dynamic-analysis-jobs'] });
    },
    onError: (error) => {
      toast.error(handleApiError(error) || 'Failed to delete job');
    }
  });

  const jobs: DynamicAnalysisJob[] = data?.jobs || [];

  const handleDeleteJob = (job: DynamicAnalysisJob) => {
    if (window.confirm(`Are you sure you want to delete "${job.name}"? This action cannot be undone.`)) {
      deleteMutation.mutate(job.id);
    }
  };

  const handleToggleStatus = (job: DynamicAnalysisJob) => {
    const isActive = job.enabled && job.status === 'active';
    toggleStatusMutation.mutate({ jobId: job.id, enable: !isActive });
  };

  const handleViewDetails = (job: DynamicAnalysisJob) => {
    navigate(`/dynamic-analysis/jobs/${job.id}`);
  };

  const handleRunNow = (job: DynamicAnalysisJob) => {
    if (window.confirm('This will regenerate all dynamic analysis entries. Continue?')) {
      runJobMutation.mutate(job.id);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-destructive/15 border border-destructive/20 rounded-lg p-6">
        <h3 className="text-lg font-medium text-destructive mb-2">Error Loading Jobs</h3>
        <p className="text-destructive">Failed to load dynamic analysis jobs. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center gap-3">
        <Zap className="w-8 h-8 text-warn" />
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Auto Dynamic Analysis
          </h1>
          <p className="text-muted-foreground mt-1">
            Automatically generate optimized analysis configurations for your financial accounts
          </p>
        </div>
      </div>

      {/* Header Actions */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-foreground">
            Analysis Jobs
          </h2>
          <p className="text-muted-foreground mt-1">
            All dynamic analysis jobs in your organization ({jobs.length})
          </p>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-warn/15 rounded-lg">
              <Layers className="w-5 h-5 text-warn" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Jobs</p>
              <p className="text-2xl font-bold text-foreground">{jobs.length}</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-success/15 rounded-lg">
              <CheckCircle className="w-5 h-5 text-success" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Active</p>
              <p className="text-2xl font-bold text-foreground">
                {jobs.filter(j => j.enabled && j.status === 'active').length}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-muted/20 rounded-lg">
              <Clock className="w-5 h-5 text-muted-foreground" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Inactive</p>
              <p className="text-2xl font-bold text-foreground">
                {jobs.filter(j => !j.enabled || j.status === 'inactive').length}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-destructive/15 rounded-lg">
              <AlertCircle className="w-5 h-5 text-destructive" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Errors</p>
              <p className="text-2xl font-bold text-foreground">
                {jobs.filter(j => j.status === 'error').length}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Jobs List */}
      {jobs.length === 0 ? (
        <Card className="p-12 text-center">
          <Zap className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">
            No Dynamic Analysis Jobs
          </h3>
          <p className="text-muted-foreground">
            Dynamic analysis jobs are created and managed from the Pulse section.
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {jobs.map((job) => (
            <JobCard
              key={job.id}
              job={job}
              onDelete={handleDeleteJob}
              onToggleStatus={handleToggleStatus}
              onViewDetails={handleViewDetails}
              onRunNow={handleRunNow}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default DynamicAnalysisPage;

